import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import MyApplications from './MyApplications';
import ScholarshipCard from './ScholarshipCard';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../seo/SEO';

const ScholarshipPage = () => {
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appliedScholarshipIds, setAppliedScholarshipIds] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    const fetchScholarships = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'scholarships'));
        const scholarshipData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setScholarships(scholarshipData);
      } catch (error) {
        console.error('Error fetching scholarships:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserApplications = async () => {
      if (!user) return; // Only fetch if user is logged in
      try {
        const applicationsQuery = query(collection(db, `users/${user.uid}/applications`));
        const applicationsSnapshot = await getDocs(applicationsQuery);
        const appliedIds = applicationsSnapshot.docs.map(doc => doc.data().scholarshipId);
        setAppliedScholarshipIds(appliedIds);
      } catch (error) {
        console.error('Error fetching user applications:', error);
      }
    };

    fetchScholarships();
    fetchUserApplications();
  }, [user]); // Re-run if user changes

  const defaultScholarships = [
    {
      id: 'fellow-scholarship',
      title: 'Become a Fellow Scholarship',
      type: 'fellow',
      eligibility: 'Nigerian University Students from 2nd level until penultimate year in Education related courses',
      benefits: [
        'Year tuition cost coverage',
        'Exam stipends',
        'Opportunity to become a mentor'
      ],
      requiredDocuments: [
        'Valid Student ID',
        'Academic Transcript',
        'Letter of Recommendation (Academic)',
        'Statement of Purpose',
        'Research Proposal'
      ],
      description: 'This scholarship is designed to support outstanding Nigerian university students who demonstrate academic excellence and leadership potential.'
    },
    {
      id: 'utme-scholarship',
      title: 'UTME Scholar Project',
      type: 'utme',
      eligibility: 'Students in Senior Secondary School with interest in Education related courses',
      benefits: [
        'UTME fee coverage',
        'Post UTME fee coverage',
        'First year tuition fee coverage'
      ],
      requiredDocuments: [
        'WAEC/NECO Result',
        'School ID',
        'Letter of Recommendation (Academic)',
        'Statement of Purpose',
      ],
      description: 'This scholarship supports promising secondary school students in their journey to higher education by covering their examination and initial tuition fees.'
    },
    {
      id: 'orphan-scholarship',
      title: 'Orphan Project',
      type: 'orphan',
      eligibility: 'Orphaned children in Schools in need of financial Support',
      benefits: [
        'Tuition fee coverage',
        'Educational materials support',
        'Mentorship program access'
      ],
      requiredDocuments: [
        'Death Certificate(s) of Parent(s)',
        'Guardian\'s ID',
        'School ID',
        'Academic Records',
        'Letter of Recommendation (Academic)',
      ],
      description: 'This scholarship provides financial support and mentorship to orphaned children, helping them pursue their education without financial barriers.'
    }
  ];

  const getScholarshipStatus = (scholarship) => {
    const now = new Date();
    const startDate = new Date(scholarship.startDate);
    const endDate = new Date(scholarship.endDate);

    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'closed';
    return 'open';
  };

  // Scholarship schema
  const scholarshipSchema = {
    "@context": "https://schema.org",
    "@type": "Scholarship",
    "name": "Intellectual Oasis Fellowship Scholarships",
    "description": "Comprehensive scholarship programs supporting Nigerian students, secondary school candidates, and orphaned learners in their educational journey.",
    "provider": {
      "@type": "Organization",
      "name": "Intellectual Oasis Fellowship",
      "url": "https://iofellowship.org",
      "sameAs": [
        "https://www.facebook.com/profile.php?id=61561270017439",
        "https://www.instagram.com/io_fellowship/",
        "https://www.linkedin.com/company/intellectual-oasis-fellowship"
      ]
    },
    "eligibilityDescription": "Scholarships available for university students, secondary school candidates, and orphaned learners."
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Scholarships - Intellectual Oasis Fellowship"
        description="Explore scholarship opportunities from Intellectual Oasis Fellowship. Support for university students, UTME scholars, and orphaned learners. Apply now for financial assistance and mentorship."
        pathname="/scholarships"
        image="/images/scholarships-og.jpg"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(scholarshipSchema)}
        </script>
      </Helmet>
      <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-center mb-8">Available Scholarships</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {defaultScholarships.map(scholarship => {
          const scholarshipData = scholarships.find(s => s.id === scholarship.id) || {};
          const mergedScholarship = { ...scholarship, ...scholarshipData };
          const status = getScholarshipStatus(mergedScholarship);
          const isApplicationPeriodActive = status === 'open';

          return (
            <ScholarshipCard 
              key={scholarship.id} 
              scholarship={mergedScholarship}
              status={status}
              isApplicationPeriodActive={isApplicationPeriodActive}
              hasApplied={appliedScholarshipIds.includes(scholarship.id)}
            />
          );
        })}
      </div>

      <div className="mt-12">
        <h2 className="text-3xl font-bold text-center mb-8">My Applications</h2>
        <MyApplications />
      </div>
    </div>
    </>
  );
};

export default ScholarshipPage; 